import { QueryClient, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { ProjectListRequest, ProjectRead } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";
import { objectEntries, removeNullUndefined } from "@nens/shared-utils";

import { createUpdateForQueries } from "./hooks/create-updater-for-queries";
import { QueryListReturn, QueryReturn } from "./types/query-return";
import { fetchAllPages } from "./utils/fetch-all-pages";

// Single

type ProjectListRequestFilters = Pick<ProjectListRequest, "status" | "code" | "slug">;
type ProjectFilters = ProjectListRequestFilters | Pick<ProjectRead, "id"> | undefined;

// Because this query works by filters like slug and id. We have to do a bit more complicated fetching code to get the project
const getProjectQuery = (queryClient: QueryClient, filters: ProjectFilters) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["project", "stat", filters],
    queryFn: async () => {
      if (!filters) throw Error("Never happens");

      // Helper: Returns project if all filters match, else null
      const findMatching = (project: ProjectRead) =>
        objectEntries(filters).every(
          ([key, value]) => project[key as keyof typeof project] === value
        )
          ? project
          : null;

      // Try to get existing project list from cache
      const cachedProjectList = queryClient.getQueryData<QueryReturn<typeof getProjectListQuery>>(
        getProjectListQuery().queryKey
      )?.items;

      if (!cachedProjectList) {
        // Search cache for a matching project query
        const projectQueryKeyPrefix = getProjectQuery(queryClient, undefined).queryKey.slice(0, -1);
        const allQueries = queryClient.getQueryCache().findAll({
          queryKey: projectQueryKeyPrefix,
          exact: false,
        });

        const ownQueryFound = allQueries.find((query) => {
          const data = query.state.data;
          if (!data) return false;
          return findMatching(data as ProjectRead) !== null;
        });

        if (ownQueryFound) {
          return ownQueryFound.state.data as ProjectRead;
        }

        // If no cached project(s) and filters have id, fetch the single project directly
        if ("id" in filters) {
          return await getRanaApiV1().ProjectsApi.projectRetrieve({ projectId: filters.id });
        }
      }

      // Use cached list or fetch list filtered by filters
      const projectList =
        cachedProjectList ??
        (await getRanaApiV1().ProjectsApi.projectList(filters as ProjectListRequestFilters)).items;

      // Find projects matching all filters, remove nulls, and return first match
      const matchedProjects = removeNullUndefined(projectList.map(findMatching));

      return matchedProjects[0] ?? null;
    },
    enabled: !!filters,
  });
};
export const useProjectQuery = (filters: ProjectFilters) => {
  const queryClient = useQueryClient();
  return useQuery(getProjectQuery(queryClient, filters));
};

export const useProjectsQueries = (filtersArray: ProjectFilters[]) => {
  const queryClient = useQueryClient();
  return useQueries({
    queries: filtersArray.map((filters) => getProjectQuery(queryClient, filters)),
  });
};

// LIST

const getProjectListQuery = () => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["projects", "list"],
    queryFn: async () => {
      return fetchAllPages(getRanaApiV1().ProjectsApi.projectList);
    },
  });
};

export const useProjectListQuery = () => {
  return useQuery(getProjectListQuery());
};

// INVALIDATION

export const useInvalidateProjectListQuery = () => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: getProjectListQuery().queryKey,
      refetchType: "all",
    });
  };
};

export const useUpdateProjectQueries = () => {
  const queryClient = useQueryClient();
  return createUpdateForQueries<QueryListReturn<typeof getProjectListQuery>>(queryClient, {
    getSingleQueryKey: () => getProjectQuery(queryClient, undefined).queryKey.slice(0, -1),
    getListQueryKey: () => getProjectListQuery().queryKey,
  });
};
